import {
  ENGAGEMENT_ACTION_REF_KEY,
  ENGAGEMENT_SCHEMA_REF_KEY,
  type StoredToolSchemaMappingRefs,
  claimsEngagementAction,
  parseEngagementActionType,
  readEngagementActionType,
  requiresEngagementIdMapping,
  withToolEngagementAction,
  withoutToolEngagementAction,
} from '@reach/shared-types/tool-engagement';
import type { ToolResponseProjection } from '@reach/shared-types/tool-response-projection';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { partnerApi } from '../../lib/api';
import { schemaDefinitionKeys } from '../../lib/schema-definition-query-keys';
import actionStyles from './EngagementAction.module.css';
import { EngagementOutcomeCards } from './EngagementOutcomeCards';
import { ENGAGEMENT_ACTION_COPY, unreadableEngagementActionMessage } from './engagement-action-copy';
import { ToolFormField as Field } from './ToolFormField';
import { ToolFormSection as Section } from './ToolFormSection';
import formStyles from './ToolForm.module.css';

const UNCLASSIFIED = '';
const SCHEMA_DEFINITIONS_STALE_TIME_MS = 5 * 60 * 1000;
const SCHEMA_DEFINITIONS_GC_TIME_MS = 10 * 60 * 1000;

interface EngagementActionSectionProps {
  step: number;
  schemaMappingRefs: StoredToolSchemaMappingRefs | null;
  responseMapping: ToolResponseProjection | null;
  onChange: (schemaMappingRefs: StoredToolSchemaMappingRefs | null) => void;
  onValidityChange: (isValid: boolean) => void;
}

export const EngagementActionSection = ({
  step,
  schemaMappingRefs,
  responseMapping,
  onChange,
  onValidityChange,
}: EngagementActionSectionProps) => {
  const actionType = readEngagementActionType(schemaMappingRefs);
  const declaredActionType = schemaMappingRefs?.[ENGAGEMENT_ACTION_REF_KEY] ?? UNCLASSIFIED;
  const selectedSchemaId = schemaMappingRefs?.[ENGAGEMENT_SCHEMA_REF_KEY] ?? '';

  const schemaDefinitionsQuery = useQuery({
    queryKey: schemaDefinitionKeys.list(),
    queryFn: () => partnerApi.getSchemaDefinitions(),
    enabled: actionType !== null,
    staleTime: SCHEMA_DEFINITIONS_STALE_TIME_MS,
    gcTime: SCHEMA_DEFINITIONS_GC_TIME_MS,
  });
  const { data: schemaDefinitions } = schemaDefinitionsQuery;

  const activeSchemas = (schemaDefinitions ?? []).filter(schema => !schema.deactivated_at);
  const selectedSchemaIsAvailable =
    selectedSchemaId === '' || activeSchemas.some(schema => schema.id === selectedSchemaId);
  const schemasAreLoading =
    actionType !== null && schemaDefinitions === undefined && schemaDefinitionsQuery.isFetching;
  const schemasFailedToLoad =
    actionType !== null && schemaDefinitionsQuery.isError && !schemaDefinitionsQuery.isFetching;

  const setActionType = (value: string) => {
    const nextActionType = parseEngagementActionType(value);
    if (!nextActionType) {
      const remainingRefs = withoutToolEngagementAction(schemaMappingRefs);
      onChange(Object.keys(remainingRefs).length > 0 ? remainingRefs : null);
      return;
    }
    onChange(
      withToolEngagementAction({
        schemaMappingRefs,
        actionType: nextActionType,
        engagementSchemaDefinitionId: selectedSchemaId,
      }),
    );
  };

  const setSchemaId = (schemaDefinitionId: string) => {
    if (!actionType) return;
    onChange(
      withToolEngagementAction({
        schemaMappingRefs,
        actionType,
        engagementSchemaDefinitionId: schemaDefinitionId,
      }),
    );
  };

  const classificationStarted = claimsEngagementAction(schemaMappingRefs);
  const actionTypeIsMissing = classificationStarted && declaredActionType === UNCLASSIFIED;
  const actionTypeIsUnreadable = declaredActionType !== UNCLASSIFIED && actionType === null;
  const schemaIsMissing = actionType !== null && selectedSchemaId === '';
  const schemaIsUnavailable =
    schemaDefinitions !== undefined && selectedSchemaId !== '' && !selectedSchemaIsAvailable;
  const engagementIdIsMissing = requiresEngagementIdMapping(schemaMappingRefs, responseMapping);
  const requiredSchemasAreLoaded =
    actionType === null || (schemaDefinitions !== undefined && !schemaDefinitionsQuery.isError);
  const classificationIsComplete =
    !actionTypeIsMissing &&
    !actionTypeIsUnreadable &&
    !schemaIsMissing &&
    !schemaIsUnavailable &&
    !engagementIdIsMissing &&
    requiredSchemasAreLoaded;

  useEffect(() => {
    onValidityChange(classificationIsComplete);
  }, [classificationIsComplete, onValidityChange]);

  const schemaPrompt = actionType === null ? null : ENGAGEMENT_ACTION_COPY.schemaPrompts[actionType];
  const displayedOutcomeValue =
    actionTypeIsMissing || actionTypeIsUnreadable ? null : declaredActionType;

  return (
    <Section
      step={step}
      title={ENGAGEMENT_ACTION_COPY.sectionTitle}
      subtitle={ENGAGEMENT_ACTION_COPY.sectionSubtitle}>
      <EngagementOutcomeCards selectedValue={displayedOutcomeValue} onChange={setActionType} />
      {actionTypeIsMissing && (
        <span className={formStyles.fieldError} role="alert">
          {ENGAGEMENT_ACTION_COPY.missingAction}
        </span>
      )}
      {actionTypeIsUnreadable && (
        <span className={formStyles.fieldError} role="alert">
          {unreadableEngagementActionMessage(declaredActionType)}
        </span>
      )}

      {actionType !== null && schemaPrompt && (
        <>
          <Field label={schemaPrompt.label} hint={schemaPrompt.hint}>
            <select
              value={selectedSchemaId}
              onChange={event => setSchemaId(event.target.value)}
              className={formStyles.select}
              disabled={schemaDefinitions === undefined || schemaDefinitionsQuery.isError}
              aria-label={schemaPrompt.label}>
              <option value="">{ENGAGEMENT_ACTION_COPY.chooseSchema}</option>
              {selectedSchemaId !== '' && schemaDefinitions === undefined && (
                <option value={selectedSchemaId}>{ENGAGEMENT_ACTION_COPY.savedSchema}</option>
              )}
              {schemaIsUnavailable && (
                <option value={selectedSchemaId} disabled>
                  {ENGAGEMENT_ACTION_COPY.savedSchemaUnavailable}
                </option>
              )}
              {activeSchemas.map(schema => (
                <option key={schema.id} value={schema.id}>
                  {schema.plural_name || schema.name}
                </option>
              ))}
            </select>
            {schemaIsMissing && (
              <span className={formStyles.fieldError} role="alert">
                {ENGAGEMENT_ACTION_COPY.missingSchema}
              </span>
            )}
            {schemaIsUnavailable && (
              <span className={formStyles.fieldError} role="alert">
                {ENGAGEMENT_ACTION_COPY.unavailableSchema}
              </span>
            )}
          </Field>

          {schemasAreLoading && (
            <p className={formStyles.helpText}>{ENGAGEMENT_ACTION_COPY.loadingSchemas}</p>
          )}

          {schemasFailedToLoad && (
            <p className={formStyles.fieldError} role="alert">
              {ENGAGEMENT_ACTION_COPY.schemaLoadFailure}{' '}
              <button
                type="button"
                className={formStyles.linkButton}
                onClick={() => void schemaDefinitionsQuery.refetch()}>
                {ENGAGEMENT_ACTION_COPY.retrySchemaLoad}
              </button>
              .
            </p>
          )}

          {schemaDefinitions !== undefined && activeSchemas.length === 0 && (
            <p className={formStyles.helpText}>{ENGAGEMENT_ACTION_COPY.noActiveSchemas}</p>
          )}

          <p className={actionStyles.explanation}>
            {ENGAGEMENT_ACTION_COPY.actionExplanations[actionType]}
          </p>
        </>
      )}
    </Section>
  );
};
